import React, {useEffect, useLayoutEffect, useState} from 'react';
import {ScreenProps} from '../../../../interfaces/screens/mainScreen';
import MainLayout from '../../../mainLayout';
import styled, {IStyledComponent, useTheme} from 'styled-components/native';
import {Platform} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import TextGeneric from '../../../../components/textGeneric';
import {tr} from '../../../../translations';
import {hdp} from '../../../../utils/responsive';
import GenericButton from '../../../../components/button';
import {useIsFocused, useRoute} from '@react-navigation/native';
import {Theme} from '../../../../theme/lightTheme';
import {createNote, editNote} from '../actions';
import screens from '../../../../config/screens';
import Animated, {FadeInDown, FadeInUp, FadeOut} from 'react-native-reanimated';
import TextInput from '../../../../components/textInput';
import {useCustomHeaderHeight} from '../../../../utils/hooks/useHeaderHeight';
import useResponsiveDimensions from '../../../../utils/hooks/useResponsiveDimensions';
import '../../../../utils/webAnimations';

const Container: IStyledComponent<'native', any> = styled.View`
  padding-horizontal: 6%;
  height: ${({bottomInset}) => hdp(99) - bottomInset}px;
  align-self: center;
  align-items: center;
  padding-top: 5%;
`;
const Title = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s4}px;
  color: ${({theme}) => theme.text.dark};
  text-align: left;
  margin-bottom: ${({theme}) => theme.text.s4 / 2}px;
`;
const SubTitle = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s6}px;
  color: ${({theme}) => theme.text.dark};
  text-align: left;
  margin-bottom: ${({theme}) => theme.text.s6}px;
`;
const SubmitButton = styled(GenericButton)<{buttonWidth?: string}>`
  width: ${({buttonWidth}) => buttonWidth};
  border-radius: 100px;
  align-self: center;
`;
const ButtonContainer = styled.View`
  flex: 1;
  justify-content: flex-end;
`;

const mandatoryFields = ['title', 'description'];
/**
 * AddNote screen component.
 *
 * @param {ScreenProps} props - The screen component props.
 * @param {object} props.navigation - The navigation object.
 */
const AddNote: React.FC<ScreenProps> = ({navigation}) => {
  const theme = useTheme() as Theme;
  const {bottom: bottomInset} = useSafeAreaInsets();
  const headerHeight = useCustomHeaderHeight();
  const {getByScreenSize, wdp} = useResponsiveDimensions();
  const {isEdit, id, title, description, news} = useRoute<any>().params || {};
  const isFocused = useIsFocused();

  const [form, updateForm] = useState<any>({
    title: __DEV__ ? 'My Note about Medical News Briefing' : title || '',
    description: __DEV__
      ? "This reminder ensures you're always informed with the most recent and relevant medical news, helping you stay ahead in your practice or studies. Tap to review the key highlights from this week's top stories, including advancements in healthcare, pharmaceutical updates, and emerging health trends."
      : description || '',
  });
  // Define the form completion state
  const [formComplete, setFormComplete] = useState(false);

  useLayoutEffect(() => {
    if (isEdit) {
      navigation?.setOptions({
        title: 'editNotePage',
      });
    }
  }, []);

  // Function to handle changes to the form fields
  const handleChange = (key: string, value: string) => {
    updateForm({
      ...form,
      [key]: value,
    });
  };

  // Effect to check if the form is complete
  useEffect(() => {
    let _formComplete = true;
    for (let index = 0; index < mandatoryFields.length; index++) {
      const field = mandatoryFields[index];
      if (!form[field] || (form[field] || '').trim().length <= 0) {
        setFormComplete(false);
        _formComplete = false;
        break;
      }
    }
    if (_formComplete) {
      setFormComplete(true);
    }
  }, [form]);

  const submit = async () => {
    let res = false;
    if (isEdit && id) {
      res = await editNote(id, form.title, form.description);
    } else {
      res = await createNote(form.title, form.description, news);
    }
    if (res) {
      if (isEdit) {
        navigation?.navigate(screens.content.viewNote, {
          id,
          title: form.title,
          description: form.description,
          date: new Date().toISOString(),
        });
      }
      navigation?.goBack();
    }
  };

  return (
    <MainLayout
      showOverlay={false}
      enableScroll={false}
      noPadding
      backHeader
      keyboardAvoidScrollView
      noPaddingHeader>
      <Container
        style={{width: getByScreenSize('100%', '70%')}}
        bottomInset={bottomInset + headerHeight}>
        <Animated.View entering={FadeInDown.springify()} exiting={FadeOut}>
          <Title bold>
            {isEdit ? tr('addNote.pageTitleEdit') : tr('addNote.pageTitle')}
          </Title>
          <SubTitle>
            {isEdit ? tr('addNote.subtitleEdit') : tr('addNote.subtitle')}
          </SubTitle>
        </Animated.View>

        {mandatoryFields.map((label, index) => {
          return (
            <Animated.View
              style={{width: '100%'}}
              key={label}
              entering={FadeInDown.delay(300 + index * 150).springify()}
              exiting={FadeOut}>
              <TextInput
                label={label === 'title' ? tr(`addNote.${label}Title`) : ''}
                isLabelBold
                placeholder={tr(`addNote.${label}`)}
                style={[
                  label === 'description' && {
                    height: theme.text.s1 * 5,
                    textAlignVertical: 'top',
                    lineHeight: theme.text.s8 * 1.4,
                  },
                ]}
                inputContainerStyle={{
                  padding: label === 'description' ? hdp(1) : 0,
                }}
                value={form[label]}
                onChangeText={text => handleChange(label, text)}
                containerStyle={{
                  marginBottom: theme.text.s7,
                }}
                multiline={label === 'description'}
                scrollEnabled={label === 'description'}
              />
            </Animated.View>
          );
        })}

        <ButtonContainer>
          <Animated.View
            style={{width: wdp(90)}}
            {...(Platform.OS === 'web'
              ? {className: 'screen-fade-in-up'}
              : {entering: FadeInUp.delay(600), exiting: FadeOut})}>
            <SubmitButton
              buttonWidth={getByScreenSize('90%', '30%')}
              label={tr('addNote.save')}
              radius={100}
              onPress={submit}
              disabled={!formComplete}
            />
          </Animated.View>
        </ButtonContainer>
      </Container>
    </MainLayout>
  );
};

export default AddNote;
